import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PeopleIcon from '@mui/icons-material/People';
import HotelIcon from '@mui/icons-material/Hotel';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import LocalPoliceIcon from '@mui/icons-material/LocalPolice';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import DatePicker from '../../shared/components/forms/HotelDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import api, {
  getBookingList,
  getCustomerList,
  getAllRooms,
  getPaymentList,
  getPosRestaurantOrders,
  getPosOrderPayments,
  getHotelBasicDetails,
  getGstReport,
  getPoliceReport,
  getInvoiceSetup,
} from '../../services/api';
import {
  downloadPremiumReportExcel,
  downloadPremiumReportPdf,
  downloadPoliceReportExcel,
} from '../../utils/reportExport';
import { formatCountryLabel } from '../../utils/countryDisplay';
import { PERMISSIONS } from '../../auth/permissions';
import usePermission from '../../auth/usePermission';

const ACCENT = '#4ce1b6';
/** Teal accent for all report tiles and report-scoped UI (matches former Expenses card). */
const REPORT_BRAND_TEAL = '#00897B';
/** Fixed tile width to match setup / config carousel cards (compact column). */
const REPORT_TILE_WIDTH_PX = 152;
const PREVIEW_ROWS = 50;
const PAGE_SIZE = 200;

function reportTypeUsesDateFilter(rt) {
  return (
    rt === 'booking' ||
    rt === 'finance' ||
    rt === 'gst' ||
    rt === 'restaurant' ||
    rt === 'police'
  );
}

/** @returns {string|null} */
function getDateRangeValidationMessage(from, to) {
  if (!from || !to || !moment.isMoment(from) || !moment.isMoment(to)) return null;
  if (!from.isValid() || !to.isValid()) return null;
  const start = from.clone().startOf('day');
  const end = to.clone().startOf('day');
  if (end.isBefore(start, 'day')) return 'From date must be on or before To date.';
  return null;
}

/** Normalize paginated API payloads (capital `Data` vs lowercase `data`). */
function extractPageSlice(res) {
  if (res == null) return [];
  if (Array.isArray(res)) return res;
  const data = res.Data ?? res.data ?? res.results;
  return Array.isArray(data) ? data : [];
}

function extractTotalRecords(res, chunkLength) {
  if (res == null) return chunkLength;
  const t = res.total_records ?? res.total ?? res.count;
  if (typeof t === 'number' && !Number.isNaN(t)) return t;
  return chunkLength;
}

async function fetchAllPages(loadPage) {
  let page = 1;
  const all = [];
  for (let guard = 0; guard < 500; guard += 1) {
    const res = await loadPage(page);
    const chunk = extractPageSlice(res);
    const totalKnown = extractTotalRecords(res, chunk.length);
    all.push(...chunk);
    if (!chunk.length) break;
    if (chunk.length < PAGE_SIZE) break;
    if (all.length >= totalKnown) break;
    page += 1;
  }
  return all;
}

function isMeaningfulValue(v) {
  return v != null && v !== '' && v !== '—';
}

function formatAllocatedRooms(row) {
  const ar = row.allocated_rooms;
  if (!ar || !Array.isArray(ar)) return '—';
  const nums = ar
    .map((r) => r.room_number ?? r.roomNumber)
    .filter((x) => x !== null && x !== undefined && x !== '');
  return nums.length ? nums.map(String).join(', ') : '—';
}

/** Fallback when rooms not yet allocated: summarize booking line items. */
function formatRoomsCell(row) {
  const fromAlloc = formatAllocatedRooms(row);
  if (fromAlloc !== '—') return fromAlloc;
  const items = row.booking_items;
  if (!items || !Array.isArray(items) || !items.length) return '—';
  const parts = items
    .map((i) => {
      const rt = i.room_type_name || '';
      const n = i.rooms != null && i.rooms !== '' ? `×${i.rooms}` : '';
      return [rt, n].filter(Boolean).join(' ').trim();
    })
    .filter(Boolean);
  return parts.length ? parts.join('; ') : '—';
}

const REPORT_TYPES = [
  {
    id: 'booking',
    title: 'Bookings',
    subtitle: 'Reservations & stays',
    icon: CalendarMonthIcon,
    color: REPORT_BRAND_TEAL,
  },
  {
    id: 'restaurant',
    title: 'Restaurant',
    subtitle: 'POS orders & billing',
    icon: RestaurantIcon,
    color: REPORT_BRAND_TEAL,
  },
  {
    id: 'customer',
    title: 'Customers',
    subtitle: 'Guest registry',
    icon: PeopleIcon,
    color: REPORT_BRAND_TEAL,
  },
  {
    id: 'inventory',
    title: 'Inventory',
    subtitle: 'Rooms by type',
    icon: HotelIcon,
    color: REPORT_BRAND_TEAL,
  },
  {
    id: 'finance',
    title: 'Payments',
    subtitle: 'Incoming payment lines',
    icon: AccountBalanceIcon,
    color: REPORT_BRAND_TEAL,
  },
  {
    id: 'gst',
    title: 'GST',
    subtitle: 'Tax invoice register',
    icon: RequestQuoteIcon,
    color: REPORT_BRAND_TEAL,
  },
  {
    id: 'police',
    title: 'Police log',
    subtitle: 'In-stay & departed—verified profiles',
    icon: LocalPoliceIcon,
    color: REPORT_BRAND_TEAL,
  },
];

const BOOKING_STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'checked_in', label: 'Checked in' },
  { value: 'checked_out', label: 'Checked out' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'no_show', label: 'No show' },
];

/** GST register: B2B = guest has GSTIN, B2C = no GSTIN. */
const GST_PARTY_TYPE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'b2b', label: 'B2B' },
  { value: 'b2c', label: 'B2C' },
];

const GST_BILL_TYPE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'room', label: 'Room Bill GST' },
  { value: 'food', label: 'Food Bill GST' },
];

const GST_ALL_COLUMNS = [
  { key: 'reservation_id', label: 'Booking ID' },
  { key: 'invoice_no', label: 'Invoice ID' },
  { key: 'invoice_date', label: 'Invoice Date' },
  { key: 'guest_name', label: 'Guest Name' },
  { key: 'guest_gstin', label: 'Guest GSTIN' },
  { key: 'room_taxable_amount', label: 'Room Taxable Amount' },
  { key: 'room_gst_rate', label: 'Room GST Rate' },
  { key: 'room_cgst_percent', label: 'Room CGST %' },
  { key: 'room_cgst_amount', label: 'Room CGST' },
  { key: 'room_sgst_percent', label: 'Room SGST %' },
  { key: 'room_sgst_amount', label: 'Room SGST' },
  { key: 'room_igst_percent', label: 'Room IGST %' },
  { key: 'room_igst_amount', label: 'Room IGST' },
  { key: 'room_total', label: 'Room Total' },
  { key: 'food_taxable_amount', label: 'Food/Restaurant Taxable Amount' },
  { key: 'food_gst_rate', label: 'Food/Restaurant GST Rate' },
  { key: 'food_cgst_percent', label: 'Food/Restaurant CGST %' },
  { key: 'food_cgst_amount', label: 'Food/Restaurant CGST' },
  { key: 'food_sgst_percent', label: 'Food/Restaurant SGST %' },
  { key: 'food_sgst_amount', label: 'Food/Restaurant SGST' },
  { key: 'food_igst_percent', label: 'Food/Restaurant IGST %' },
  { key: 'food_igst_amount', label: 'Food/Restaurant IGST' },
  { key: 'food_total', label: 'Food/Restaurant Total' },
  { key: 'combined_taxable_amount', label: 'Combined Taxable Amount' },
  { key: 'combined_cgst_amount', label: 'Combined CGST' },
  { key: 'combined_sgst_amount', label: 'Combined SGST' },
  { key: 'combined_igst_amount', label: 'Combined IGST' },
  { key: 'combined_gst', label: 'Combined GST' },
  { key: 'grand_total', label: 'Grand Total' },
  { key: 'payment_status', label: 'Payment Status' },
];

function gstRowHasGuestGstin(row) {
  const gstin = String(row?.guest_gstin || '').trim();
  return Boolean(gstin) && gstin !== '—';
}

function filterGstReportRows(rows, partyType) {
  const mode = String(partyType || 'all').toLowerCase();
  if (mode === 'b2b') return rows.filter(gstRowHasGuestGstin);
  if (mode === 'b2c') return rows.filter((row) => !gstRowHasGuestGstin(row));
  return rows;
}

const RESTAURANT_ORDER_STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'placed', label: 'Placed' },
  { value: 'preparing', label: 'Preparing' },
  { value: 'served', label: 'Served' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const RESTAURANT_ORDER_TYPE_OPTIONS = [
  { value: '', label: 'All order types' },
  { value: 'dine_in', label: 'Dine in' },
  { value: 'room_service', label: 'Room service' },
  { value: 'takeaway', label: 'Takeaway' },
];

const RESTAURANT_PAYMENT_COLLECTION_OPTIONS = [
  { value: '', label: 'All collection modes' },
  { value: 'collect_now', label: 'Collect now' },
  { value: 'due', label: 'Due' },
];

const PAYMENT_REPORT_SCOPE_OPTIONS = [
  { value: 'hotel', label: 'Hotel payments' },
  { value: 'restaurant', label: 'Restaurant payments' },
];

const RESTAURANT_PAYMENT_STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'captured', label: 'Captured' },
  { value: 'failed', label: 'Failed' },
  { value: 'refunded', label: 'Refunded' },
];

function buildBookingRows(raw, dateFrom, dateTo) {
  const from = dateFrom ? moment(dateFrom).startOf('day') : null;
  const to = dateTo ? moment(dateTo).endOf('day') : null;
  return raw
    .filter((b) => {
      if (!from || !to) return true;
      const ci = b.check_in_date ? moment(b.check_in_date) : null;
      if (!ci || !ci.isValid()) return false;
      return ci.isBetween(from, to, 'day', '[]');
    })
    .map((b) => ({
      code: b.code || '—',
      guest_name: b.guest_name || '—',
      guest_email: b.guest_email || '—',
      guest_phone: b.guest_phone || '—',
      purpose_of_stay: formatBookingPurposeOfStay(b),
      check_in_date: b.check_in_date || '—',
      check_out_date: b.check_out_date || '—',
      status: b.status || b.status_value || '—',
      channel: b.channel || '—',
      booking_type:
        (b.booking_type_display && String(b.booking_type_display).trim() && b.booking_type_display !== '—')
          ? String(b.booking_type_display).trim()
          : b.booking_type
            ? String(b.booking_type)
                .split('_')
                .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                .join(' ')
            : '—',
      booking_created: bookingCreatedHotelTime(b),
      rooms: formatRoomsCell(b),
      nights: b.nights ?? '—',
      total_amount: b.total_amount != null ? Number(b.total_amount).toFixed(2) : '—',
      currency: b.currency || '',
      payment_status: b.payment_status || '—',
    }));
}

/** Single line: Address 1–2, city, state, country (full name), PIN, location summary. */
function formatCustomerCombinedAddress(c) {
  const parts = [];
  const push = (v) => {
    if (isMeaningfulValue(v)) parts.push(String(v).trim());
  };
  push(c.address_line1);
  push(c.address_line2);
  push(c.city);
  push(c.state);
  if (isMeaningfulValue(c.country)) push(formatCountryLabel(c.country));
  push(c.pincode);
  if (isMeaningfulValue(c.location)) push(c.location);
  return parts.length ? parts.join(', ') : '—';
}

function buildCustomerRows(raw) {
  return raw.map((c) => ({
    id: c.id != null ? String(c.id) : '—',
    name: c.name || '—',
    email: c.email || '—',
    phone: c.phone || '—',
    address: formatCustomerCombinedAddress(c),
    document_type: isMeaningfulValue(c.document_type) ? c.document_type : '—',
    loyalty_member_no: c.loyalty_member_no || '—',
    total_bookings: c.total_bookings != null ? String(c.total_bookings) : '—',
    total_spent: c.total_spent != null ? Number(c.total_spent).toFixed(2) : '—',
    currency: c.currency || '',
    last_booking: c.last_booking || '—',
    status: c.status || '—',
  }));
}

function buildInventoryRows(roomTypes) {
  const rows = [];
  const groups = Array.isArray(roomTypes) ? roomTypes : [];
  groups.forEach((rt) => {
    const rtInfo = rt.room_type || rt.roomType || {};
    const typeName = rtInfo.name || rt.name || '—';
    const code = rtInfo.code ?? rt.code ?? '';
    const roomList = rt.rooms || rt.room_list || [];
    roomList.forEach((r) => {
      rows.push({
        room_type: typeName,
        type_code: code ? String(code) : '—',
        room_number: r.room_number != null && r.room_number !== '' ? String(r.room_number) : '—',
        floor: r.floor != null && r.floor !== '' ? String(r.floor) : '—',
        status: r.status || '—',
        clean_status: r.clean_status != null && r.clean_status !== '' ? String(r.clean_status) : '—',
        is_checkout: r.is_checkout === true ? 'Yes' : r.is_checkout === false ? 'No' : '—',
        is_available:
          r.is_available === true ? 'Yes' : r.is_available === false ? 'No' : '—',
      });
    });
  });
  return rows;
}

function buildPaymentRows(raw) {
  return raw.map((p) => ({
    booking_code: p.booking_code || '—',
    guest_name: p.guest_name || '—',
    payment_date: p.payment_date || '—',
    amount: p.amount != null ? Number(p.amount).toFixed(2) : '—',
    currency: p.currency || '',
    payment_type: p.payment_type || '—',
    payment_method: p.payment_method || '—',
    payment_status: p.payment_status || '—',
    reference_number: p.reference_number || p.provider_txn_id || '—',
    nights: p.nights != null ? String(p.nights) : '—',
  }));
}

function buildRestaurantPaymentRows(raw, dateFrom, dateTo, filters = {}) {
  const from = dateFrom ? moment(dateFrom).startOf('day') : null;
  const to = dateTo ? moment(dateTo).endOf('day') : null;
  const methodFilter = String(filters.paymentMethod || '').trim().toLowerCase();
  const statusFilter = String(filters.status || '').trim().toLowerCase();
  const searchText = String(filters.search || '').trim().toLowerCase();

  return (Array.isArray(raw) ? raw : [])
    .filter((payment) => {
      const collectedAt = payment?.collected_at ? moment(payment.collected_at) : null;
      if (from && to) {
        if (!collectedAt || !collectedAt.isValid() || !collectedAt.isBetween(from, to, undefined, '[]')) {
          return false;
        }
      }

      const paymentMethod = String(payment?.payment_method || '').trim().toLowerCase();
      if (methodFilter && paymentMethod !== methodFilter) return false;

      const paymentStatus = String(payment?.status || '').trim().toLowerCase();
      if (statusFilter && paymentStatus !== statusFilter) return false;

      if (!searchText) return true;
      const searchable = [
        payment?.order_number,
        payment?.source_label,
        payment?.room_number,
        payment?.table_name,
        payment?.payment_method,
        payment?.note,
      ]
        .map((value) => String(value || '').toLowerCase())
        .join(' ');
      return searchable.includes(searchText);
    })
    .map((payment) => {
      const sourceLabel = String(payment?.source_label || '').trim();
      const roomNumber = String(payment?.room_number || '').trim();
      const tableName = String(payment?.table_name || '').trim();
      let location = sourceLabel;
      if (!location) {
        if (roomNumber && tableName) location = `Room ${roomNumber} / ${tableName}`;
        else if (roomNumber) location = `Room ${roomNumber}`;
        else if (tableName) location = tableName;
        else location = '—';
      }

      return {
        order_number: payment?.order_number || '—',
        payment_date: payment?.collected_at ? moment(payment.collected_at).format('YYYY-MM-DD HH:mm') : '—',
        location,
        payment_method: payment?.payment_method || '—',
        amount: formatAmount2(payment?.amount),
        payment_status: formatLabelFromKey(payment?.status),
        note: payment?.note || '—',
      };
    });
}

function formatLabelFromKey(raw) {
  const val = String(raw || '').trim();
  if (!val) return '—';
  return val
    .replace(/_/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function toNumberSafe(value, fallback = 0) {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
}

function formatAmount2(value) {
  return toNumberSafe(value, 0).toFixed(2);
}

function formatRestaurantTaxBreakdown(taxBreakdown) {
  if (!Array.isArray(taxBreakdown) || !taxBreakdown.length) return '—';
  const chunks = taxBreakdown
    .map((row) => {
      if (!row || typeof row !== 'object') return '';
      const label = String(row.label || row.tax_name || row.name || '').trim() || 'Tax';
      const rateDisplay =
        String(row.rate_display || '').trim() ||
        (row.rate != null ? `${toNumberSafe(row.rate).toFixed(2)}%` : '') ||
        (row.percentage != null ? `${toNumberSafe(row.percentage).toFixed(2)}%` : '');
      const amount = formatAmount2(row.amount ?? row.tax_amount ?? 0);
      return `${label}${rateDisplay ? ` (${rateDisplay})` : ''}: ${amount}`;
    })
    .filter(Boolean);
  return chunks.length ? chunks.join(' | ') : '—';
}

function formatRestaurantLocation(order) {
  const roomNumber = String(order?.room_number || '').trim();
  const tableName = String(order?.table_name || '').trim();
  if (roomNumber && tableName) return `Room ${roomNumber} / ${tableName}`;
  if (roomNumber) return `Room ${roomNumber}`;
  if (tableName) return tableName;
  return '—';
}

function buildRestaurantRows(raw, dateFrom, dateTo, filters = {}) {
  const from = dateFrom ? moment(dateFrom).startOf('day') : null;
  const to = dateTo ? moment(dateTo).endOf('day') : null;
  const searchText = String(filters.search || '').trim().toLowerCase();
  const paymentCollectionMode = String(filters.paymentCollectionMode || '').trim().toLowerCase();
  const paymentMethod = String(filters.paymentMethod || '').trim().toLowerCase();
  const statusFilter = String(filters.status || '').trim().toLowerCase();
  const orderTypeFilter = String(filters.orderType || '').trim().toLowerCase();
  return (Array.isArray(raw) ? raw : [])
    .filter((order) => {
      const createdAt = order?.created_at ? moment(order.created_at) : null;
      if (from && to) {
        if (!createdAt || !createdAt.isValid() || !createdAt.isBetween(from, to, undefined, '[]')) {
          return false;
        }
      }
      const orderStatus = String(order?.status || '').trim().toLowerCase();
      if (statusFilter && orderStatus !== statusFilter) return false;
      const orderType = String(order?.order_type || '').trim().toLowerCase();
      if (orderTypeFilter && orderType !== orderTypeFilter) return false;
      const collectionMode = String(order?.payment_collection_mode || '').trim().toLowerCase();
      if (paymentCollectionMode && collectionMode !== paymentCollectionMode) return false;
      const orderPaymentMethod = String(order?.payment_method || '').trim().toLowerCase();
      if (paymentMethod && orderPaymentMethod !== paymentMethod) return false;
      if (!searchText) return true;
      const searchable = [
        order?.order_number,
        order?.customer_name,
        order?.customer_phone,
        order?.room_number,
        order?.table_name,
        order?.notes,
      ]
        .map((v) => String(v || '').toLowerCase())
        .join(' ');
      return searchable.includes(searchText);
    })
    .map((order) => {
      const subtotal = toNumberSafe(order?.subtotal, 0);
      const discountValue = toNumberSafe(order?.discount_value, 0);
      const discountType = String(order?.discount_type || '').trim().toLowerCase();
      const discountAmount = discountType === 'percentage' ? (subtotal * discountValue) / 100 : discountValue;
      const deliveryCharge = toNumberSafe(order?.delivery_charge, 0);
      const containerCharge = toNumberSafe(order?.container_charge, 0);
      const itemRows = Array.isArray(order?.items) ? order.items : [];
      const itemQuantity = itemRows.reduce((sum, item) => sum + toNumberSafe(item?.quantity, 0), 0);
      const phoneCode = String(order?.customer_phone_country_code || '').trim();
      const phone = String(order?.customer_phone || '').trim();
      return {
        order_number: order?.order_number || '—',
        order_date: order?.created_at ? moment(order.created_at).format('YYYY-MM-DD HH:mm') : '—',
        status: formatLabelFromKey(order?.status),
        order_type: formatLabelFromKey(order?.order_type),
        booking_reference: order?.booking != null ? String(order.booking) : '—',
        guest_name: order?.customer_name || '—',
        guest_phone: phone ? `${phoneCode ? `${phoneCode} ` : ''}${phone}` : '—',
        location: formatRestaurantLocation(order),
        item_count: String(itemRows.length),
        item_qty: itemQuantity.toFixed(2),
        subtotal: formatAmount2(subtotal),
        discount_type: formatLabelFromKey(order?.discount_type),
        discount_value: formatAmount2(discountValue),
        discount_amount: formatAmount2(discountAmount),
        tax_amount: formatAmount2(order?.tax_amount),
        tax_breakdown: formatRestaurantTaxBreakdown(order?.tax_breakdown),
        delivery_charge: formatAmount2(deliveryCharge),
        container_charge: formatAmount2(containerCharge),
        additional_charges: formatAmount2(deliveryCharge + containerCharge),
        total_amount: formatAmount2(order?.total_amount),
        payment_collection_mode: formatLabelFromKey(order?.payment_collection_mode),
        payment_method: order?.payment_method || '—',
        notes: order?.notes || '—',
      };
    });
}

/** Booking created timestamp in hotel timezone (API), else legacy created_at string. */
function bookingCreatedHotelTime(booking) {
  const d = booking.created_at_display;
  if (d && String(d).trim() && d !== '—') return String(d).trim();
  return booking.created_at || '—';
}

/** Purpose of stay for reports: label + note when Other. */
function formatBookingPurposeOfStay(b) {
  const disp = b.purpose_of_stay_display;
  const code = b.purpose_of_stay;
  const note = (b.purpose_of_stay_note || '').trim();
  const label =
    disp && String(disp).trim() && disp !== '—'
      ? String(disp).trim()
      : code && String(code).trim()
        ? String(code).trim()
        : '';
  if (!label) return '—';
  const isOther =
    String(code || '').toLowerCase() === 'other' ||
    String(label).toLowerCase() === 'other';
  if (isOther && note) return `${label}: ${note}`;
  return label;
}

/** Columns to show when `hiddenKeys` lists keys to omit; never returns empty. */
function columnsMinusHidden(columnsDef, hiddenKeys) {
  const hidden = new Set(hiddenKeys ?? []);
  const vis = columnsDef.filter((c) => !hidden.has(c.key));
  return vis.length ? vis : columnsDef;
}

export default function ReportsHub() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { can } = usePermission();
  const canUseRestaurantReports = can(PERMISSIONS.POS_RESTAURANT_MANAGE);

  const [reportType, setReportType] = useState('booking');
  const [dateFrom, setDateFrom] = useState(() => {
    const today = moment().startOf('day');
    // On the 1st, month-start === today; use prior month so the filter has a 1-month span.
    if (today.date() === 1) return today.clone().subtract(1, 'month');
    return today.clone().startOf('month');
  });
  const [dateTo, setDateTo] = useState(() => moment().startOf('day'));
  const [bookingStatus, setBookingStatus] = useState('');
  const [bookingSearch, setBookingSearch] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerActive, setCustomerActive] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [paymentReportScope, setPaymentReportScope] = useState('hotel');
  const [restaurantPaymentEntryStatus, setRestaurantPaymentEntryStatus] = useState('');
  const [restaurantPaymentEntryMethod, setRestaurantPaymentEntryMethod] = useState('');
  const [restaurantPaymentEntrySearch, setRestaurantPaymentEntrySearch] = useState('');
  const [restaurantStatus, setRestaurantStatus] = useState('');
  const [restaurantOrderType, setRestaurantOrderType] = useState('');
  const [restaurantSearch, setRestaurantSearch] = useState('');
  const [restaurantPaymentCollectionMode, setRestaurantPaymentCollectionMode] = useState('');
  const [restaurantPaymentMethod, setRestaurantPaymentMethod] = useState('');
  const [policeIdNumberMode, setPoliceIdNumberMode] = useState('masked');
  const [policeRoomNumber, setPoliceRoomNumber] = useState('');
  const [policeMergeRanges, setPoliceMergeRanges] = useState({ booking: [], room: [] });
  const [gstPartyType, setGstPartyType] = useState('all');
  const [gstBillType, setGstBillType] = useState('room');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hotelName, setHotelName] = useState('');
  const [columnPickerOpen, setColumnPickerOpen] = useState(false);
  /** Per-report list of column keys to hide from preview & exports. */
  const [hiddenColumnKeysByReport, setHiddenColumnKeysByReport] = useState({});
  const previewFetchGenRef = React.useRef(0);
  const visibleReportTypes = useMemo(
    () => REPORT_TYPES.filter((report) => report.id !== 'restaurant' || canUseRestaurantReports),
    [canUseRestaurantReports]
  );
  const visiblePaymentReportScopeOptions = useMemo(
    () => PAYMENT_REPORT_SCOPE_OPTIONS.filter((scope) => (
      scope.value !== 'restaurant' || canUseRestaurantReports
    )),
    [canUseRestaurantReports]
  );

  React.useEffect(() => {
    if (!canUseRestaurantReports && reportType === 'restaurant') {
      setReportType('booking');
    }
    if (!canUseRestaurantReports && paymentReportScope === 'restaurant') {
      setPaymentReportScope('hotel');
    }
  }, [canUseRestaurantReports, paymentReportScope, reportType]);

  React.useEffect(() => {
    getHotelBasicDetails()
      .then((d) => setHotelName(d?.hotel_name || d?.name || ''))
      .catch(() => setHotelName(''));
  }, []);

  React.useEffect(() => {
    previewFetchGenRef.current += 1;
    setRows([]);
    setPoliceMergeRanges({ booking: [], room: [] });
    setError(null);
    setLoading(false);
  }, [reportType, paymentReportScope]);

  React.useEffect(() => {
    if (reportType !== 'police') return;
    setRows((prev) =>
      prev.map((row) => ({
        ...row,
        id_number:
          policeIdNumberMode === 'unmasked'
            ? row.id_number_full || '—'
            : row.id_number_masked || '—',
      }))
    );
  }, [policeIdNumberMode, reportType]);

  const columns = useMemo(() => {
    switch (reportType) {
      case 'booking':
        return [
          { key: 'code', label: 'Booking' },
          { key: 'guest_name', label: 'Guest' },
          { key: 'guest_email', label: 'Email' },
          { key: 'guest_phone', label: 'Phone' },
          { key: 'purpose_of_stay', label: 'Purpose of stay' },
          { key: 'check_in_date', label: 'Check-in' },
          { key: 'check_out_date', label: 'Check-out' },
          { key: 'status', label: 'Status' },
          { key: 'channel', label: 'Channel' },
          { key: 'booking_type', label: 'Booking type' },
          { key: 'booking_created', label: 'Booked (hotel time)' },
          { key: 'rooms', label: 'Rooms' },
          { key: 'nights', label: 'Nights' },
          { key: 'total_amount', label: 'Total' },
          { key: 'currency', label: 'Cur' },
          { key: 'payment_status', label: 'Pay status' },
        ];
      case 'customer':
        return [
          { key: 'id', label: 'ID' },
          { key: 'name', label: 'Name' },
          { key: 'email', label: 'Email' },
          { key: 'phone', label: 'Phone' },
          { key: 'address', label: 'Address' },
          { key: 'document_type', label: 'ID type' },
          { key: 'loyalty_member_no', label: 'Loyalty #' },
          { key: 'total_bookings', label: 'Bookings' },
          { key: 'total_spent', label: 'Spent' },
          { key: 'currency', label: 'Cur' },
          { key: 'last_booking', label: 'Last stay' },
          { key: 'status', label: 'Status' },
        ];
      case 'inventory':
        return [
          { key: 'room_type', label: 'Room type' },
          { key: 'type_code', label: 'Code' },
          { key: 'room_number', label: 'Room #' },
          { key: 'floor', label: 'Floor' },
          { key: 'status', label: 'Status' },
          { key: 'clean_status', label: 'Cleaning' },
          { key: 'is_available', label: 'Avail.' },
          { key: 'is_checkout', label: 'From CO' },
        ];
      case 'finance':
        if (paymentReportScope === 'restaurant') {
          return [
            { key: 'order_number', label: 'Order #' },
            { key: 'payment_date', label: 'Collected at' },
            { key: 'location', label: 'Source' },
            { key: 'payment_method', label: 'Method' },
            { key: 'amount', label: 'Amount' },
            { key: 'payment_status', label: 'Status' },
            { key: 'note', label: 'Note' },
          ];
        }
        return [
          { key: 'booking_code', label: 'Booking' },
          { key: 'guest_name', label: 'Guest' },
          { key: 'payment_date', label: 'Date' },
          { key: 'amount', label: 'Amount' },
          { key: 'currency', label: 'Cur' },
          { key: 'payment_type', label: 'Type' },
          { key: 'payment_method', label: 'Method' },
          { key: 'payment_status', label: 'Status' },
          { key: 'reference_number', label: 'Reference' },
          { key: 'nights', label: 'Nights' },
        ];
      case 'gst':
        if (gstBillType === 'all') return GST_ALL_COLUMNS;
        return [
          { key: 'invoice_no', label: 'Invoice No' },
          { key: 'invoice_date', label: 'Invoice Date' },
          { key: 'reservation_id', label: 'Reservation ID' },
          { key: 'guest_name', label: 'Guest Name' },
          { key: 'guest_gstin', label: 'Guest GSTIN' },
          { key: 'state', label: 'State' },
          ...(gstBillType === 'food'
            ? [
                { key: 'food_revenue', label: 'Food Revenue' },
                { key: 'other_charges', label: 'Other Charges' },
              ]
            : [{ key: 'room_revenue', label: 'Room Revenue' }]),
          { key: 'taxable_amount', label: 'Taxable Amount' },
          { key: 'cgst_percent', label: 'CGST %' },
          { key: 'cgst_amount', label: 'CGST Amount' },
          { key: 'sgst_percent', label: 'SGST %' },
          { key: 'sgst_amount', label: 'SGST Amount' },
          { key: 'igst_percent', label: 'IGST %' },
          { key: 'igst_amount', label: 'IGST Amount' },
          { key: 'total_gst', label: 'Total GST' },
          { key: 'invoice_total', label: 'Invoice Total' },
          { key: 'payment_mode', label: 'Payment Mode' },
          { key: 'payment_status', label: 'Payment Status' },
        ];
      case 'restaurant':
        return [
          { key: 'order_number', label: 'Order #' },
          { key: 'order_date', label: 'Order date' },
          { key: 'status', label: 'Status' },
          { key: 'order_type', label: 'Order type' },
          { key: 'booking_reference', label: 'Booking ID' },
          { key: 'guest_name', label: 'Guest' },
          { key: 'guest_phone', label: 'Phone' },
          { key: 'location', label: 'Location' },
          { key: 'subtotal', label: 'Subtotal' },
          { key: 'discount_amount', label: 'Discount amount' },
          { key: 'tax_amount', label: 'Tax amount' },
          { key: 'additional_charges', label: 'Additional charges' },
          { key: 'total_amount', label: 'Total amount' },
          { key: 'payment_collection_mode', label: 'Collection mode' },
          { key: 'payment_method', label: 'Payment method' },
        ];
      case 'police':
        return [
          { key: 'sr', label: '#' },
          { key: 'booking_code', label: 'Booking ID' },
          { key: 'booking_date', label: 'Booking Date' },
          { key: 'room_number', label: 'Room No.' },
          { key: 'adults', label: 'Adult' },
          { key: 'children', label: 'Child' },
          { key: 'guest_name', label: 'Guest Name' },
          { key: 'phone', label: 'Mobile Number' },
          { key: 'gender', label: 'Gender' },
          { key: 'date_of_birth', label: 'Date of Birth' },
          { key: 'id_document_type', label: 'ID Type' },
          { key: 'id_number', label: 'ID Number' },
          { key: 'purpose_of_stay', label: 'Purpose of stay' },
          { key: 'nationality', label: 'Nationality' },
          { key: 'address', label: 'Address' },
          { key: 'check_in', label: 'Check-in' },
          { key: 'check_out', label: 'Check-out' },
          { key: 'booking_source', label: 'Booking Source' },
          { key: 'email', label: 'Email' },
          { key: 'status', label: 'Status' },
        ];
      default:
        return [];
    }
  }, [reportType, paymentReportScope, gstBillType]);

  const reportColumnKey =
    reportType === 'finance'
      ? `finance:${paymentReportScope}`
      : reportType === 'gst'
        ? `gst:${gstBillType}`
        : reportType;
  const hiddenKeysMain = hiddenColumnKeysByReport[reportColumnKey] ?? [];

  const visibleColumns = useMemo(
    () => columnsMinusHidden(columns, hiddenKeysMain),
    [columns, hiddenKeysMain]
  );

  const dateRangeInvalidMessage = useMemo(() => {
    if (!reportTypeUsesDateFilter(reportType)) return null;
    return getDateRangeValidationMessage(dateFrom, dateTo);
  }, [reportType, dateFrom, dateTo]);

  const dateFilterBlocksActions = Boolean(dateRangeInvalidMessage);

  const toggleColumnHidden = (reportKey, columnDefs, colKey, makeVisible) => {
    setHiddenColumnKeysByReport((prev) => {
      const hidden = new Set(prev[reportKey] ?? []);
      if (!makeVisible) {
        const visibleKeys = columnDefs.filter((c) => !hidden.has(c.key));
        if (visibleKeys.length === 1 && visibleKeys[0].key === colKey) return prev;
        hidden.add(colKey);
      } else {
        hidden.delete(colKey);
      }
      return { ...prev, [reportKey]: [...hidden] };
    });
  };

  const selectAllColumnsForKeys = (keysToClear) => {
    setHiddenColumnKeysByReport((prev) => {
      const next = { ...prev };
      keysToClear.forEach((k) => {
        next[k] = [];
      });
      return next;
    });
  };

  const runQuickView = useCallback(async () => {
    const genAtStart = previewFetchGenRef.current;
    if (reportTypeUsesDateFilter(reportType)) {
      const msg = getDateRangeValidationMessage(dateFrom, dateTo);
      if (msg) {
        setError(msg);
        setRows([]);
        return;
      }
    }
    setLoading(true);
    setError(null);
    try {
      if (reportType === 'booking') {
        const raw = await fetchAllPages((page) =>
          getBookingList({
            page,
            per_page: PAGE_SIZE,
            sort: '-check_in_date',
            ...(bookingStatus ? { status: bookingStatus } : {}),
            ...(bookingSearch ? { search: bookingSearch } : {}),
            check_in_from: dateFrom.format('YYYY-MM-DD'),
            check_in_to: dateTo.format('YYYY-MM-DD'),
          })
        );
        if (genAtStart !== previewFetchGenRef.current) return;
        setRows(buildBookingRows(raw, dateFrom, dateTo));
      } else if (reportType === 'customer') {
        const raw = await fetchAllPages((page) => {
          const params = {
            page,
            per_page: PAGE_SIZE,
            sort: '-created_at',
          };
          if (customerName) params.name = customerName;
          if (customerActive === '1') params.is_active = true;
          else if (customerActive === '0') params.is_active = false;
          return getCustomerList(params);
        });
        if (genAtStart !== previewFetchGenRef.current) return;
        setRows(buildCustomerRows(raw));
      } else if (reportType === 'inventory') {
        const data = (await getAllRooms({})) || {};
        const groups = data.room_types ?? data.roomTypes ?? [];
        if (genAtStart !== previewFetchGenRef.current) return;
        setRows(buildInventoryRows(groups));
      } else if (reportType === 'finance') {
        if (paymentReportScope === 'restaurant') {
          const raw = await getPosOrderPayments({
            ...(restaurantPaymentEntryMethod
              ? { payment_method: restaurantPaymentEntryMethod }
              : {}),
            ...(restaurantPaymentEntryStatus ? { status: restaurantPaymentEntryStatus } : {}),
          });
          if (genAtStart !== previewFetchGenRef.current) return;
          setRows(
            buildRestaurantPaymentRows(raw, dateFrom, dateTo, {
              status: restaurantPaymentEntryStatus,
              paymentMethod: restaurantPaymentEntryMethod,
              search: restaurantPaymentEntrySearch,
            })
          );
        } else {
          const raw = await fetchAllPages((page) =>
            getPaymentList({
              page,
              per_page: PAGE_SIZE,
              sort: '-payment_date',
              payment_date_from: dateFrom.format('YYYY-MM-DD'),
              payment_date_to: dateTo.format('YYYY-MM-DD'),
              ...(paymentStatus ? { payment_status: paymentStatus } : {}),
            })
          );
          if (genAtStart !== previewFetchGenRef.current) return;
          setRows(buildPaymentRows(raw));
        }
      } else if (reportType === 'gst') {
        const res = await getGstReport({
          date_from: dateFrom.format('YYYY-MM-DD'),
          date_to: dateTo.format('YYYY-MM-DD'),
          bill_type: gstBillType,
        });
        if (genAtStart !== previewFetchGenRef.current) return;
        const gstRows = Array.isArray(res?.data) ? res.data : [];
        setRows(filterGstReportRows(gstRows, gstPartyType));
      } else if (reportType === 'restaurant') {
        const rawOrders = await getPosRestaurantOrders({
          ...(restaurantStatus ? { status: restaurantStatus } : {}),
          ...(restaurantOrderType ? { order_type: restaurantOrderType } : {}),
        });
        if (genAtStart !== previewFetchGenRef.current) return;
        setRows(
          buildRestaurantRows(rawOrders, dateFrom, dateTo, {
            status: restaurantStatus,
            orderType: restaurantOrderType,
            search: restaurantSearch,
            paymentCollectionMode: restaurantPaymentCollectionMode,
            paymentMethod: restaurantPaymentMethod,
          })
        );
      } else if (reportType === 'police') {
        const res = await getPoliceReport({
          date_from: dateFrom.format('YYYY-MM-DD'),
          date_to: dateTo.format('YYYY-MM-DD'),
          id_number_mode: policeIdNumberMode,
          ...(bookingSearch ? { search: bookingSearch } : {}),
          ...(bookingStatus ? { status: bookingStatus } : {}),
          ...(policeRoomNumber ? { room_number: policeRoomNumber } : {}),
        });
        if (genAtStart !== previewFetchGenRef.current) return;
        setRows(Array.isArray(res?.data) ? res.data : []);
        setPoliceMergeRanges(res?.merge_ranges || { booking: [], room: [] });
      }
    } catch (e) {
      if (genAtStart !== previewFetchGenRef.current) return;
      setError(e.userMessage || e.message || 'Failed to load report data');
      setRows([]);
    } finally {
      if (genAtStart === previewFetchGenRef.current) setLoading(false);
    }
  }, [
    reportType,
    dateFrom,
    dateTo,
    bookingStatus,
    bookingSearch,
    customerName,
    customerActive,
    paymentStatus,
    paymentReportScope,
    restaurantPaymentEntryStatus,
    restaurantPaymentEntryMethod,
    restaurantPaymentEntrySearch,
    restaurantStatus,
    restaurantOrderType,
    restaurantSearch,
    restaurantPaymentCollectionMode,
    restaurantPaymentMethod,
    policeIdNumberMode,
    policeRoomNumber,
    gstPartyType,
    gstBillType,
  ]);

  const subtitle = useMemo(() => {
    const h = hotelName ? `${hotelName} · ` : '';
    const scopeTag =
      reportType === 'finance'
        ? ` · ${paymentReportScope === 'restaurant' ? 'Restaurant Payments' : 'Hotel Payments'}`
        : reportType === 'gst'
          ? ` · ${
              gstBillType === 'food'
                ? 'Food Bill GST'
                : gstBillType === 'all'
                  ? 'All (Room + Food)'
                  : 'Room Bill GST'
            }`
          : reportType === 'police'
            ? ' · Police log'
            : '';
    return `${h}${dateFrom.format('MMM D, YYYY')} – ${dateTo.format('MMM D, YYYY')}${scopeTag}`;
  }, [hotelName, dateFrom, dateTo, reportType, paymentReportScope, gstBillType]);

  const exportBaseName = useMemo(() => {
    const slug =
      reportType === 'finance'
        ? `finance-${paymentReportScope}`
        : reportType === 'gst'
          ? `gst-${gstBillType}`
          : reportType;
    return `nexa-report-${slug}-${moment().format('YYYY-MM-DD')}`;
  }, [reportType, paymentReportScope, gstBillType]);

  const handlePdf = async () => {
    if (!rows.length) return;
    if (reportTypeUsesDateFilter(reportType)) {
      const msg = getDateRangeValidationMessage(dateFrom, dateTo);
      if (msg) {
        setError(msg);
        return;
      }
    }
    const title =
      visibleReportTypes.find((r) => r.id === reportType)?.title || 'Report';
    setError(null);
    try {
      const hotelPart = hotelName ? String(hotelName).trim() : 'RoomNexa';

      // Hotel logo only (no RoomNexa fallback): invoice logo → property/NexaStay logo
      let hotelLogoUrl = '';
      try {
        const setup = await getInvoiceSetup();
        hotelLogoUrl = String(setup?.settings?.invoice_logo_url || '').trim();
      } catch {
        hotelLogoUrl = '';
      }
      if (!hotelLogoUrl) {
        try {
          const { data } = await api.get('/distribution/api/nexastay-theme/');
          hotelLogoUrl = String(data?.logo_url || '').trim();
        } catch {
          hotelLogoUrl = '';
        }
      }
      await downloadPremiumReportPdf(
        title,
        subtitle,
        visibleColumns,
        rows,
        `${exportBaseName}.pdf`,
        {
          landscape:
            visibleColumns.length > 7 || reportType === 'gst' || reportType === 'police',
          fontSize: reportType === 'gst' || reportType === 'police' ? 6 : 7,
          hotelName: hotelPart,
          reportType,
          hotelLogoUrl,
        }
      );
    } catch (e) {
      setError(e.userMessage || e.message || 'PDF export failed');
    }
  };

  const handleExcel = async () => {
    if (!rows.length) return;
    if (reportTypeUsesDateFilter(reportType)) {
      const msg = getDateRangeValidationMessage(dateFrom, dateTo);
      if (msg) {
        setError(msg);
        return;
      }
    }
    setError(null);
    try {
      const reportTitle =
        visibleReportTypes.find((r) => r.id === reportType)?.title || 'Report';
      const hotelPart = hotelName ? String(hotelName).trim() : 'RoomNexa';
      if (reportType === 'police') {
        await downloadPoliceReportExcel(
          visibleColumns,
          rows,
          `${exportBaseName}.xlsx`,
          {
            title: `${hotelPart} — Police Report`,
            subtitle,
            mergeRanges: policeMergeRanges,
          }
        );
        return;
      }
      await downloadPremiumReportExcel(
        visibleColumns,
        rows,
        `${exportBaseName}.xlsx`,
        {
          sheetName: reportTitle,
          title: `${hotelPart} — ${reportTitle}`,
          subtitle,
        }
      );
    } catch (e) {
      setError(e.userMessage || e.message || 'Excel export failed');
    }
  };

  const previewSlice = rows.slice(0, PREVIEW_ROWS);
  const selectedMeta = visibleReportTypes.find((r) => r.id === reportType);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={{ width: '100%', pb: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 4 },
            mb: 3,
            borderRadius: 3,
            background: isDark
              ? `linear-gradient(135deg, ${alpha(ACCENT, 0.12)} 0%, ${alpha('#1a1a22', 0.95)} 45%, #232329 100%)`
              : `linear-gradient(135deg, ${alpha(ACCENT, 0.2)} 0%, #f8fffc 40%, #ffffff 100%)`,
            border: `1px solid ${alpha(ACCENT, isDark ? 0.25 : 0.35)}`,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: -40,
              right: -20,
              width: 180,
              height: 180,
              borderRadius: '50%',
              background: alpha(ACCENT, 0.08),
              pointerEvents: 'none',
            }}
          />
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em', mb: 0.5 }}>
            Reports studio
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ width: '100%', maxWidth: 'none', lineHeight: 1.5, position: 'relative', zIndex: 1 }}
          >
            Filter, preview, and export bookings, guests, inventory, payment lines, restaurant POS orders,
            and expense transactions to PDF or Excel, including the police log for in-stay & departed stays
            with ID and address from linked customer profiles.
          </Typography>
          {hotelName ? (
            <Chip
              label={hotelName}
              size="small"
              sx={{ mt: 2, bgcolor: alpha(ACCENT, 0.15), color: 'text.primary', fontWeight: 600 }}
            />
          ) : null}
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2, md: 2.5 },
            mb: 3,
            borderRadius: 2,
            borderColor: alpha(ACCENT, isDark ? 0.35 : 0.28),
            bgcolor: isDark ? alpha(ACCENT, 0.06) : alpha(ACCENT, 0.06),
            borderLeft: `4px solid ${ACCENT}`,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25, color: 'text.primary' }}>
            Regulatory acknowledgment
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65, mb: 1.5 }}>
            Accurate guest registers and timely statutory filings are part of normal hotel operations. The
            points below summarize common obligations; confirm requirements with your legal counsel or local
            authorities, as rules vary by location.
          </Typography>
          <Stack component="ul" spacing={1.25} sx={{ m: 0, pl: 2.25, listStyleType: 'disc' }}>
            <Typography component="li" variant="body2" color="text.secondary" sx={{ lineHeight: 1.65, display: 'list-item' }}>
              <Typography component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Foreign guests (FRRO / police):
              </Typography>{' '}
              Form C must be submitted to the local Foreigners Regional Registration Officer (FRRO) or the
              nearest police station, in line with applicable regulations.
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary" sx={{ lineHeight: 1.65, display: 'list-item' }}>
              <Typography component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Police inspections:
              </Typography>{' '}
              Law enforcement may inspect guest registers. Export the police log here (PDF or Excel) when
              disclosure is required for a lawful request or inspection.
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.75, lineHeight: 1.55 }}>
            Reports generated here support record-keeping and audit readiness only; they do not replace
            official filings, signatures, or professional legal advice.
          </Typography>
        </Paper>

        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600 }}>
          1 · Choose report
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: `repeat(auto-fill, minmax(${REPORT_TILE_WIDTH_PX}px, ${REPORT_TILE_WIDTH_PX}px))`,
            },
            justifyContent: { xs: 'stretch', lg: 'center' },
            columnGap: { xs: 1.25, sm: 2 },
            rowGap: { xs: 1.25, sm: 2 },
            mb: 3,
            maxWidth: '100%',
          }}
        >
          {visibleReportTypes.map((rt) => {
            const Icon = rt.icon;
            const active = reportType === rt.id;
            const teal = REPORT_BRAND_TEAL;
            return (
              <Card
                key={rt.id}
                elevation={0}
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  mx: 0,
                  borderRadius: 3,
                  border: `2px solid ${active ? teal : alpha(theme.palette.divider, isDark ? 0.55 : 0.85)}`,
                  bgcolor: active ? alpha(teal, isDark ? 0.14 : 0.07) : 'background.paper',
                  transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease, background-color 0.2s ease',
                  boxShadow: active
                    ? `0 0 0 1px ${alpha(teal, 0.4)}, 0 10px 28px ${alpha(teal, isDark ? 0.22 : 0.14)}`
                    : 'none',
                  '&:hover': {
                    borderColor: alpha(teal, 0.65),
                    transform: 'translateY(-3px)',
                    boxShadow: `0 12px 32px ${alpha(teal, isDark ? 0.18 : 0.12)}`,
                  },
                }}
              >
                <CardActionArea
                  onClick={() => setReportType(rt.id)}
                  sx={{
                    py: { xs: 1.5, sm: 2.75 },
                    px: { xs: 0.75, sm: 1.5 },
                    minHeight: { xs: 132, sm: 168 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    textAlign: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 44, sm: 56 },
                      height: { xs: 44, sm: 56 },
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      bgcolor: isDark ? alpha(teal, 0.22) : alpha(teal, 0.14),
                      color: teal,
                      mb: { xs: 1, sm: 1.75 },
                    }}
                  >
                    <Icon sx={{ fontSize: { xs: 22, sm: 28 } }} />
                  </Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight={800}
                    sx={{
                      lineHeight: 1.25,
                      mb: 0.75,
                      color: 'text.primary',
                      letterSpacing: '-0.02em',
                      fontSize: { xs: '0.78rem', sm: '1rem' },
                    }}
                  >
                    {rt.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      lineHeight: 1.45,
                      px: 0.75,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      maxWidth: '100%',
                    }}
                  >
                    {rt.subtitle}
                  </Typography>
                </CardActionArea>
              </Card>
            );
          })}
        </Box>

        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600 }}>
          2 · Filters
        </Typography>
        <Paper
          variant="outlined"
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: 2,
            borderColor: alpha(ACCENT, 0.2),
          }}
        >
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
              {reportType !== 'inventory' && reportType !== 'customer' && (
                <>
                  <DatePicker
                    label="From"
                    value={dateFrom}
                    onChange={(v) => {
                      if (!v) return;
                      setDateFrom(v);
                      if (dateTo.isBefore(v, 'day')) setDateTo(v.clone());
                    }}
                    maxDate={dateTo}
                    slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
                  />
                  <DatePicker
                    label="To"
                    value={dateTo}
                    onChange={(v) => {
                      if (!v) return;
                      let next = v.clone();
                      if (next.isBefore(dateFrom, 'day')) next = dateFrom.clone();
                      setDateTo(next);
                    }}
                    minDate={dateFrom}
                    slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
                  />
                </>
              )}
              {reportType === 'booking' ? (
                <>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={bookingStatus}
                      label="Status"
                      onChange={(e) => setBookingStatus(e.target.value)}
                    >
                      {BOOKING_STATUS_OPTIONS.map((o) => (
                        <MenuItem key={o.value || 'all'} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    label="Search (code, guest, phone)"
                    value={bookingSearch}
                    onChange={(e) => setBookingSearch(e.target.value)}
                    sx={{ minWidth: 220 }}
                  />
                </>
              ) : null}
              {reportType === 'gst' ? (
                <>
                  <FormControl size="small" sx={{ minWidth: 170 }}>
                    <InputLabel>Bill type</InputLabel>
                    <Select
                      value={gstBillType}
                      label="Bill type"
                      onChange={(e) => setGstBillType(e.target.value)}
                    >
                      {GST_BILL_TYPE_OPTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Party type</InputLabel>
                    <Select
                      value={gstPartyType}
                      label="Party type"
                      onChange={(e) => setGstPartyType(e.target.value)}
                    >
                      {GST_PARTY_TYPE_OPTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              ) : null}
              {reportType === 'police' ? (
                <>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={bookingStatus}
                      label="Status"
                      onChange={(e) => setBookingStatus(e.target.value)}
                    >
                      <MenuItem value="">In-stay & departed</MenuItem>
                      {BOOKING_STATUS_OPTIONS.filter((o) => o.value).map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    label="Search (code, guest, phone)"
                    value={bookingSearch}
                    onChange={(e) => setBookingSearch(e.target.value)}
                    sx={{ minWidth: 220 }}
                  />
                  <TextField
                    size="small"
                    label="Room number"
                    value={policeRoomNumber}
                    onChange={(e) => setPoliceRoomNumber(e.target.value)}
                    sx={{ minWidth: 140 }}
                  />
                  <FormControl size="small" sx={{ minWidth: 210 }}>
                    <InputLabel>ID number mode</InputLabel>
                    <Select
                      value={policeIdNumberMode}
                      label="ID number mode"
                      onChange={(e) => setPoliceIdNumberMode(e.target.value)}
                    >
                      <MenuItem value="masked">Masked ID Number</MenuItem>
                      <MenuItem value="unmasked">Full ID Number (unmasked)</MenuItem>
                    </Select>
                  </FormControl>
                </>
              ) : null}
              {reportType === 'customer' ? (
                <>
                  <TextField
                    size="small"
                    label="Name contains"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    sx={{ minWidth: 180 }}
                  />
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Active</InputLabel>
                    <Select
                      value={customerActive}
                      label="Active"
                      onChange={(e) => setCustomerActive(e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="1">Active</MenuItem>
                      <MenuItem value="0">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </>
              ) : null}
              {reportType === 'finance' ? (
                <>
                  <Paper
                    variant="outlined"
                    sx={{
                      minWidth: { xs: '100%', md: 420 },
                      borderRadius: 2,
                      borderColor: alpha(ACCENT, 0.25),
                      overflow: 'hidden',
                    }}
                  >
                    <Tabs
                      value={paymentReportScope}
                      onChange={(_, nextValue) => setPaymentReportScope(nextValue)}
                      variant="fullWidth"
                      sx={{
                        minHeight: 42,
                        '& .MuiTabs-indicator': {
                          bgcolor: REPORT_BRAND_TEAL,
                          height: 3,
                        },
                      }}
                    >
                      {visiblePaymentReportScopeOptions.map((scope) => (
                        <Tab
                          key={scope.value}
                          value={scope.value}
                          label={scope.label}
                          sx={{
                            minHeight: 42,
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.85rem',
                          }}
                        />
                      ))}
                    </Tabs>
                  </Paper>
                  {paymentReportScope === 'restaurant' ? (
                    <>
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={restaurantPaymentEntryStatus}
                          label="Status"
                          onChange={(e) => setRestaurantPaymentEntryStatus(e.target.value)}
                        >
                          {RESTAURANT_PAYMENT_STATUS_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value || 'all'} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        label="Payment method"
                        value={restaurantPaymentEntryMethod}
                        onChange={(e) => setRestaurantPaymentEntryMethod(e.target.value)}
                        sx={{ minWidth: 170 }}
                      />
                      <TextField
                        size="small"
                        label="Search (order/source/method)"
                        value={restaurantPaymentEntrySearch}
                        onChange={(e) => setRestaurantPaymentEntrySearch(e.target.value)}
                        sx={{ minWidth: 240 }}
                      />
                    </>
                  ) : (
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel>Pay status</InputLabel>
                      <Select
                        value={paymentStatus}
                        label="Pay status"
                        onChange={(e) => setPaymentStatus(e.target.value)}
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="success">Success</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="failed">Failed</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </>
              ) : null}
              {reportType === 'restaurant' ? (
                <>
                  <FormControl size="small" sx={{ minWidth: 170 }}>
                    <InputLabel>Order status</InputLabel>
                    <Select
                      value={restaurantStatus}
                      label="Order status"
                      onChange={(e) => setRestaurantStatus(e.target.value)}
                    >
                      {RESTAURANT_ORDER_STATUS_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value || 'all'} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 170 }}>
                    <InputLabel>Order type</InputLabel>
                    <Select
                      value={restaurantOrderType}
                      label="Order type"
                      onChange={(e) => setRestaurantOrderType(e.target.value)}
                    >
                      {RESTAURANT_ORDER_TYPE_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value || 'all'} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 190 }}>
                    <InputLabel>Collection mode</InputLabel>
                    <Select
                      value={restaurantPaymentCollectionMode}
                      label="Collection mode"
                      onChange={(e) => setRestaurantPaymentCollectionMode(e.target.value)}
                    >
                      {RESTAURANT_PAYMENT_COLLECTION_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value || 'all'} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    label="Payment method"
                    value={restaurantPaymentMethod}
                    onChange={(e) => setRestaurantPaymentMethod(e.target.value)}
                    sx={{ minWidth: 180 }}
                  />
                  <TextField
                    size="small"
                    label="Search (order, guest, phone, room/table)"
                    value={restaurantSearch}
                    onChange={(e) => setRestaurantSearch(e.target.value)}
                    sx={{ minWidth: 280 }}
                  />
                </>
              ) : null}
              {reportType === 'inventory' ? (
                <Typography variant="body2" color="text.secondary">
                  Room master list uses live configuration (no date filter). For availability by
                  dates, use Inventory screens.
                </Typography>
              ) : null}
            </Stack>
            {dateRangeInvalidMessage ? (
              <Alert severity="warning" sx={{ py: 0.5 }}>
                {dateRangeInvalidMessage}
              </Alert>
            ) : null}
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <VisibilityIcon />}
                onClick={runQuickView}
                disabled={loading || dateFilterBlocksActions}
                sx={{
                  bgcolor: ACCENT,
                  color: '#0d1f1a',
                  fontWeight: 700,
                  '&:hover': { bgcolor: alpha(ACCENT, 0.85) },
                }}
              >
                Quick view
              </Button>
              <Button
                variant="outlined"
                startIcon={<ViewColumnIcon />}
                onClick={() => setColumnPickerOpen(true)}
                sx={{
                  borderColor: alpha(selectedMeta?.color || ACCENT, 0.45),
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: selectedMeta?.color || ACCENT,
                    bgcolor: alpha(selectedMeta?.color || ACCENT, isDark ? 0.1 : 0.06),
                  },
                }}
              >
                Columns
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={runQuickView}
                disabled={loading || dateFilterBlocksActions}
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<PictureAsPdfIcon />}
                onClick={handlePdf}
                disabled={!rows.length || loading || dateFilterBlocksActions}
              >
                PDF
              </Button>
              <Button
                variant="outlined"
                color="success"
                startIcon={<TableChartIcon />}
                onClick={handleExcel}
                disabled={!rows.length || loading || dateFilterBlocksActions}
              >
                Excel
              </Button>
              {rows.length > 0 ? (
                <Chip
                  label={`${rows.length} row${rows.length === 1 ? '' : 's'} loaded`}
                  size="small"
                  variant="outlined"
                />
              ) : null}
              <Chip
                label={`${visibleColumns.length}/${columns.length} columns`}
                size="small"
                sx={{
                  bgcolor: alpha(ACCENT, 0.12),
                  fontWeight: 600,
                  border: 'none',
                }}
              />
            </Stack>
          </Stack>
        </Paper>

        <Dialog
          open={columnPickerOpen}
          onClose={() => setColumnPickerOpen(false)}
          maxWidth="sm"
          fullWidth
          scroll="body"
          PaperProps={{
            sx: {
              borderRadius: 3,
              overflow: 'hidden',
              border: `1px solid ${alpha(selectedMeta?.color || ACCENT, isDark ? 0.35 : 0.28)}`,
              backgroundImage: 'none',
            },
          }}
        >
          <Box
            sx={{
              position: 'relative',
              overflow: 'hidden',
              background: isDark
                ? `linear-gradient(125deg, ${alpha(selectedMeta?.color || ACCENT, 0.2)} 0%, ${alpha('#1a1d24', 0.98)} 55%, #23262e 100%)`
                : `linear-gradient(125deg, ${alpha(selectedMeta?.color || ACCENT, 0.22)} 0%, ${alpha(ACCENT, 0.12)} 42%, #ffffff 100%)`,
              borderBottom: `1px solid ${alpha(ACCENT, 0.25)}`,
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: -30,
                right: -24,
                width: 120,
                height: 120,
                borderRadius: '50%',
                bgcolor: alpha(selectedMeta?.color || ACCENT, 0.12),
                pointerEvents: 'none',
              }}
            />
            <DialogTitle
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                pt: 2.5,
                pb: 1,
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  bgcolor: alpha(selectedMeta?.color || ACCENT, 0.2),
                  color: selectedMeta?.color || ACCENT,
                }}
              >
                <ViewColumnIcon />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                  Shape your report
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  Toggle fields for preview, PDF, and Excel. At least one column stays on.
                </Typography>
              </Box>
            </DialogTitle>
          </Box>
          <DialogContent sx={{ pt: 2.5, pb: 1 }}>
            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {selectedMeta?.title} report
                </Typography>
                <Chip
                  size="small"
                  label={`${visibleColumns.length} / ${columns.length}`}
                  sx={{ height: 24, fontWeight: 700, bgcolor: alpha(ACCENT, 0.14) }}
                />
              </Stack>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                  gap: 1,
                }}
              >
                {columns.map((col) => {
                  const hidden = new Set(hiddenKeysMain);
                  const checked = !hidden.has(col.key);
                  return (
                    <FormControlLabel
                      key={col.key}
                      control={
                        <Checkbox
                          checked={checked}
                          onChange={(_, v) => toggleColumnHidden(reportColumnKey, columns, col.key, v)}
                          sx={{
                            py: 0.5,
                            color: alpha(selectedMeta?.color || ACCENT, 0.55),
                            '&.Mui-checked': { color: selectedMeta?.color || ACCENT },
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" fontWeight={checked ? 600 : 500} color="text.primary">
                          {col.label}
                        </Typography>
                      }
                      sx={{
                        m: 0,
                        px: 1.25,
                        py: 0.5,
                        pr: 1.5,
                        borderRadius: 2,
                        alignItems: 'center',
                        border: `1px solid ${alpha(theme.palette.divider, isDark ? 0.35 : 0.18)}`,
                        bgcolor: checked ? alpha(ACCENT, isDark ? 0.1 : 0.07) : alpha(theme.palette.action.hover, 0.04),
                        transition: 'background-color 0.15s ease, border-color 0.15s ease',
                        '&:hover': {
                          borderColor: alpha(selectedMeta?.color || ACCENT, 0.45),
                          bgcolor: alpha(selectedMeta?.color || ACCENT, isDark ? 0.12 : 0.08),
                        },
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              px: 2.5,
              py: 2,
              gap: 1,
              flexWrap: 'wrap',
              bgcolor: isDark ? alpha(ACCENT, 0.04) : alpha(ACCENT, 0.05),
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            }}
          >
            <Button
              size="medium"
              onClick={() => selectAllColumnsForKeys([reportColumnKey])}
              sx={{ fontWeight: 600 }}
            >
              Show all columns
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button variant="outlined" onClick={() => setColumnPickerOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => setColumnPickerOpen(false)}
              sx={{
                fontWeight: 700,
                bgcolor: selectedMeta?.color || ACCENT,
                color: theme.palette.getContrastText(selectedMeta?.color || ACCENT),
                '&:hover': { bgcolor: alpha(selectedMeta?.color || ACCENT, 0.88) },
              }}
            >
              Apply
            </Button>
          </DialogActions>
        </Dialog>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}

        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            borderColor: alpha(ACCENT, isDark ? 0.28 : 0.22),
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.25,
              bgcolor: isDark ? '#2a2f2d' : '#d8f0e8',
              borderBottom: `1px solid ${alpha(ACCENT, isDark ? 0.35 : 0.35)}`,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
              3 · Preview{' '}
              {rows.length > PREVIEW_ROWS ? (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  (first {PREVIEW_ROWS} of {rows.length})
                </Typography>
              ) : null}
            </Typography>
          </Box>
          <TableContainer
            sx={{
              maxHeight: 440,
              bgcolor: 'background.paper',
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {visibleColumns.map((c) => (
                    <TableCell
                      key={c.key}
                      sx={{
                        fontWeight: 700,
                        backgroundColor: isDark ? '#1f2422' : '#c5e8d9',
                        color: 'text.primary',
                        borderBottom: `2px solid ${ACCENT}`,
                        zIndex: 2,
                      }}
                    >
                      {c.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
            <TableBody>
              {!previewSlice.length ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length || 1}>
                    <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                      Run <strong>Quick view</strong> to load data, then export PDF or Excel.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                previewSlice.map((row, idx) => (
                  <TableRow key={idx} hover sx={{ '&:nth-of-type(even)': { bgcolor: alpha(ACCENT, 0.03) } }}>
                    {visibleColumns.map((c) => (
                      <TableCell key={c.key} sx={{ maxWidth: 220, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {row[c.key] ?? '—'}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </Paper>

        <Divider sx={{ my: 3 }} />
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.55, display: 'block' }}>
          Export files contain the complete result set from your most recent Quick view; the table above
          shows a limited preview only.
          Use <strong>Columns</strong> to choose which fields are included in preview and exports. Use the{' '}
          <strong>Restaurant</strong> report for POS order-level billing data, and <strong>Payments</strong> tabs for both hotel payment rows
          and restaurant payment collections. Guest register exports list in-stay & departed stays for which
          a customer profile exists; address and identification fields are taken from that profile.
        </Typography>
      </Box>
    </LocalizationProvider>
  );
}
